import { useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useActivityTracker() {
  const location = useLocation();
  const lastTrackedPath = useRef('');
  const lastHeartbeat = useRef(0);
  const isUnmounting = useRef(false);

  // Get auth header if available
  const getAuthHeader = useCallback(() => {
    const token = localStorage.getItem('blkmrkt_token') || sessionStorage.getItem('blkmrkt_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  // Send heartbeat to keep session alive
  const sendHeartbeat = useCallback(async () => {
    if (isUnmounting.current) return;
    
    const now = Date.now();
    if (now - lastHeartbeat.current < 30000) return;
    lastHeartbeat.current = now;

    try {
      await axios.post(`${API}/track/heartbeat`, {}, { 
        headers: getAuthHeader(),
        timeout: 5000
      });
    } catch (error) {
      // Silently fail
    }
  }, [getAuthHeader]);

  // Track page view
  const trackPageView = useCallback(async (page) => {
    if (isUnmounting.current) return;
    
    try {
      await axios.post(`${API}/track/pageview`, { page }, { 
        headers: getAuthHeader(),
        timeout: 5000
      });
    } catch (error) {
      // Silently fail
    }
  }, [getAuthHeader]);

  // Track page views on route change - immediate, no delay
  useEffect(() => {
    isUnmounting.current = false;
    
    if (location.pathname !== lastTrackedPath.current) {
      lastTrackedPath.current = location.pathname;
      // Track immediately - don't delay
      sendHeartbeat();
      trackPageView(location.pathname);
    }
    
    return () => {
      isUnmounting.current = true;
    };
  }, [location.pathname, sendHeartbeat, trackPageView]);

  // Heartbeat interval
  useEffect(() => {
    isUnmounting.current = false;
    
    const interval = setInterval(() => {
      if (!isUnmounting.current) {
        sendHeartbeat();
      }
    }, 30000);
    
    return () => {
      isUnmounting.current = true;
      clearInterval(interval);
    };
  }, [sendHeartbeat]);

  return { sendHeartbeat, trackPageView };
}

// Product tracker - kept simple since page view tracking handles product pages
export function useProductTracker() {
  const { trackPageView } = useActivityTracker();

  const trackProductView = useCallback((productId, productName) => {
    // Page view tracking automatically detects /product/:id pages
    // and logs product name via backend lookup
  }, []);

  const trackAddToCart = useCallback((productId, productName) => {
    // Future enhancement
  }, []);

  const trackSearch = useCallback((query) => {
    // Future enhancement
  }, []);

  return { trackProductView, trackAddToCart, trackSearch };
}
